// On-screen Focus debug overlay — a dev-only analysis surface for the
// question-switching investigation. Renders only when `isFocusDebugEnabled()`
// (see focus-diag.ts: `?focusdebug`, localStorage, or window toggle).
//
// It shows, live, the exact state that drives what the user sees:
//   • the latest readiness card (per-field verdicts), and how often it's been
//     re-emitted under the same turn,
//   • the PINNED current step (what's actually on screen) vs the OLD
//     pure-derivation "first open field" — divergence here is the bug the pin
//     now absorbs,
//   • the question QUEUE (what surfaces next, in order),
//   • answers accumulated locally but NOT yet sent (the batch),
//   • the agent run/idle inference and the recent focus-diag entries, with
//     `would_have_switched` highlighted.
//
// Inline-styled on purpose: it must be unmistakably "debug", not product chrome,
// and must not touch focus.css.
//
// It is CLICK-THROUGH by default. It is a fixed overlay in the bottom-right
// corner, which is where the composer's Send button sits: with
// `pointerEvents: "auto"` on the container, the panel swallowed clicks meant for
// Send, so opening the debug tool broke the flow it was opened to debug
// (Playwright reported the overlay subtree intercepting the pointer event).
// Only the header control below opts back in, and it flips the readout region
// between click-through and interactive when you actually need to scroll or
// select text in it.

import { useEffect, useReducer, useState } from "react";

import { recentFocusDiag, type FocusDiagEntry } from "./focus-diag";

export interface FocusDebugSnapshot {
  readonly sessionId: string | null;
  readonly wsStatus: string;
  readonly sandboxPhase: string;
  /** Server-derived ground truth: is a sandbox turn executing right now? */
  readonly agentBusy: boolean;
  /** Inferred: have we sent a batch and are awaiting the agent's next card? */
  readonly thinking: boolean;
  /** Answers held locally, not yet flushed (the batch in progress). */
  readonly heldCount: number;
  readonly epoch: string;
  readonly cardId: string | null;
  /** What the raw timeline derivation yields right now. Differs from cardId
   *  while the reconnect-replay latch is holding the newest card. */
  readonly derivedCardId: string | null;
  /** How many distinct cards have been observed since this mount's seed. */
  readonly cardRevision: number;
  readonly allGreen: boolean;
  readonly verdicts: ReadonlyArray<{
    readonly field: string;
    readonly verdict: string;
    readonly hasValue: boolean;
    /** Why a non-green field is non-green (the gate's reason / the question). */
    readonly reason?: string;
  }>;
  /** Fields the user has answered themselves (the re-review/re-ask latch). */
  readonly provided: ReadonlyArray<string>;
  /** The pinned, on-screen step. */
  readonly current: { readonly field: string; readonly kind: string } | null;
  /** What the OLD pure-derivation logic would surface right now. */
  readonly derivedFirst: { readonly field: string; readonly kind: string } | null;
  /** Fields queued behind `current`, in surface order. */
  readonly queue: ReadonlyArray<string>;
  /** Locally-held answers not yet flushed to the agent. */
  readonly pendingAnswers: ReadonlyArray<{ readonly field: string; readonly text: string }>;
}

const box: React.CSSProperties = {
  position: "fixed",
  right: 8,
  bottom: 8,
  zIndex: 99999,
  width: 360,
  maxHeight: "70vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  background: "rgba(12,14,18,0.94)",
  color: "#d8dee9",
  font: "11px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace",
  border: "1px solid #3b4252",
  borderRadius: 8,
  padding: "8px 10px",
  boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
  // The container never captures pointer events — see the file header. Its own
  // controls opt back in individually.
  pointerEvents: "none",
};
// The scrolling readout. `overflow: auto` lives here rather than on `box` so the
// container can stay click-through: a scroll container that never receives
// pointer events cannot be scrolled, so the two have to be different elements.
const body: React.CSSProperties = {
  overflowY: "auto",
  minHeight: 0,
};
const headerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 8,
};
const toggleBtn: React.CSSProperties = {
  pointerEvents: "auto",
  background: "transparent",
  color: "#81a1c1",
  font: "inherit",
  border: "1px solid #3b4252",
  borderRadius: 4,
  padding: "1px 5px",
  cursor: "pointer",
};
const h: React.CSSProperties = {
  color: "#88c0d0",
  fontWeight: 700,
  margin: "8px 0 2px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontSize: 10,
};
const dim: React.CSSProperties = { color: "#7b8394" };
const warn: React.CSSProperties = { color: "#ebcb8b" };
const ok: React.CSSProperties = { color: "#a3be8c" };

const vColor = (v: string): string =>
  v === "green" ? "#a3be8c" : v === "needs_input" ? "#ebcb8b" : "#bf616a";

const sameStep = (
  a: FocusDebugSnapshot["current"],
  b: FocusDebugSnapshot["derivedFirst"],
): boolean =>
  (a === null && b === null) ||
  (a !== null && b !== null && a.field === b.field && a.kind === b.kind);

function DiagRow({ e }: { readonly e: FocusDiagEntry }) {
  const flagged = e.kind === "would_have_switched";
  return (
    <div style={{ marginBottom: 2, ...(flagged ? { color: "#ebcb8b" } : {}) }}>
      <span style={dim}>+{(e.dt / 1000).toFixed(1)}s </span>
      <span style={flagged ? warn : { color: "#81a1c1" }}>{e.kind}</span>
      {e.detail ? (
        <span style={dim}> {JSON.stringify(e.detail)}</span>
      ) : null}
    </div>
  );
}

/**
 * Live debug panel. `snap` is recomputed by RefineFlow every render; we also
 * tick on a short timer so the focus-diag tail refreshes even when RefineFlow
 * itself isn't re-rendering.
 */
export function FocusDebugPanel({ snap }: { readonly snap: FocusDebugSnapshot }) {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  // Off by default: the readout stays click-through until an operator asks for
  // it, so the panel can never be the reason a button under it stops working.
  const [interactive, setInteractive] = useState(false);
  useEffect(() => {
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const diverged = !sameStep(snap.current, snap.derivedFirst);
  const recent = recentFocusDiag(14);

  return (
    <div style={box}>
      <div style={headerRow}>
        <span style={{ ...h, marginTop: 0, color: "#8fbcbb" }}>
          focus debug · card rev {snap.cardRevision}
        </span>
        <button
          type="button"
          style={toggleBtn}
          onClick={() => setInteractive((v) => !v)}
          aria-pressed={interactive}
        >
          {interactive ? "clicks: captured" : "clicks: pass through"}
        </button>
      </div>

      {/* The dump itself is noise for a screen reader, and click-through unless
          the toggle above says otherwise. */}
      <div
        style={{ ...body, pointerEvents: interactive ? "auto" : "none" }}
        aria-hidden
        data-testid="focus-debug-body"
      >
        <div>
          <span style={dim}>ws </span>
          {snap.wsStatus}
          <span style={dim}> · sandbox </span>
          {snap.sandboxPhase}
          <span style={dim}> · agent </span>
          <span style={snap.agentBusy ? warn : ok}>
            {snap.agentBusy ? "busy" : "idle / your turn"}
          </span>
          {snap.thinking ? <span style={dim}> (awaiting reply)</span> : null}
          {snap.heldCount > 0 ? (
            <span style={{ color: "#b48ead" }}> · {snap.heldCount} held</span>
          ) : null}
        </div>
        <div style={dim}>
          epoch {snap.epoch} · card {snap.cardId ?? "—"}
          {snap.derivedCardId !== snap.cardId ? (
            <span style={warn}> · LATCHED (derived {snap.derivedCardId ?? "—"})</span>
          ) : null}
          {snap.allGreen ? <span style={ok}> · ALL GREEN</span> : null}
        </div>

        <div style={h}>on screen (pinned)</div>
        <div style={ok}>
          {snap.current ? `${snap.current.kind}: ${snap.current.field}` : "—"}
        </div>
        <div style={h}>old derivation (first open field)</div>
        <div style={diverged ? warn : dim}>
          {snap.derivedFirst
            ? `${snap.derivedFirst.kind}: ${snap.derivedFirst.field}`
            : "—"}
          {diverged ? "  (!) would have switched — pin is holding" : "  (matches)"}
        </div>

        <div style={h}>queue ({snap.queue.length})</div>
        <div style={dim}>{snap.queue.length ? snap.queue.join(" → ") : "—"}</div>

        <div style={h}>answers held (unsent batch · {snap.pendingAnswers.length})</div>
        {snap.pendingAnswers.length === 0 ? (
          <div style={dim}>—</div>
        ) : (
          snap.pendingAnswers.map((a) => (
            <div key={a.field}>
              <span style={{ color: "#b48ead" }}>{a.field}</span>
              <span style={dim}>: {a.text.slice(0, 60)}</span>
            </div>
          ))
        )}

        <div style={h}>provided (answered by user)</div>
        <div style={dim}>{snap.provided.length ? snap.provided.join(", ") : "—"}</div>

        <div style={h}>card verdicts</div>
        {snap.verdicts.length === 0 ? (
          <div style={dim}>no card yet</div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {snap.verdicts.map((v) => (
              <span key={v.field} style={{ color: vColor(v.verdict) }}>
                {v.field}={v.verdict}
                {v.hasValue ? "*" : ""}
              </span>
            ))}
          </div>
        )}
        {/* The loop signal: a field the user already answered is non-green now.
            The reason tells you WHICH vector — needs_input (agent re-asking) vs
            red/quote_not_found (grounding drift). */}
        {snap.verdicts
          .filter((v) => v.verdict !== "green" && snap.provided.includes(v.field))
          .map((v) => (
            <div key={`re-${v.field}`} style={warn}>
              (!) re-ask: {v.field}={v.verdict}
              {v.reason ? <span style={dim}> — {v.reason}</span> : null}
            </div>
          ))}
        {/* Reasons for any non-green field (answered or not). */}
        {snap.verdicts
          .filter((v) => v.verdict !== "green" && v.reason)
          .map((v) => (
            <div key={`rs-${v.field}`} style={dim}>
              {v.field}: {v.reason}
            </div>
          ))}

        <div style={h}>recent (window.__loomFocusDiag)</div>
        {recent.length === 0 ? (
          <div style={dim}>—</div>
        ) : (
          recent.map((e, i) => <DiagRow key={`${e.t}-${i}`} e={e} />)
        )}
        <div style={{ ...dim, marginTop: 6, fontSize: 10 }}>
          dumpFocusDiag() in console for the full log · (!) rows = old logic would
          have yanked the question
        </div>
      </div>
    </div>
  );
}
